import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import {
  ATTR_METHOD,
  ATTR_STORE_ID,
  ATTR_STORE_NAME,
  ATTR_HERMES_STORE,
  ATTR_HERMES_WAREHOUSE,
  buildPickupAttributes,
  rememberPickup,
  getRememberedPickup,
} from '@theme/cart-pickup-properties';

const SOURCE = 'cart-pickup';

/** @type {Promise<void>} */
let pickupQueue = Promise.resolve();

/** Nested persist calls (abort/restart) — checkout stays locked while > 0. */
let pickupPersistDepth = 0;

/**
 * @returns {boolean}
 */
const isPickupPersistInFlight = () => pickupPersistDepth > 0;

/**
 * Refresh checkout locks on every cart-pickup instance (page + drawer).
 */
const syncAllPickupCheckouts = () => {
  for (const el of document.querySelectorAll('cart-pickup')) {
    if (typeof /** @type {{ refreshCheckoutLock?: () => void }} */ (el).refreshCheckoutLock === 'function') {
      /** @type {{ refreshCheckoutLock: () => void }} */ (el).refreshCheckoutLock();
    }
  }
};

/**
 * @param {() => Promise<void>} task
 * @returns {Promise<void>}
 */
const enqueuePickupTask = (task) => {
  pickupQueue = pickupQueue.then(task, task);
  return pickupQueue;
};

/**
 * @returns {string}
 */
const getCartJsonUrl = () => {
  const cartUrl = Theme.routes.cart_url;
  return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
};

/**
 * @returns {string[]}
 */
const getCartSectionIds = () => {
  const ids = new Set();

  for (const item of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.add(item.dataset.sectionId);
    }
  }

  return [...ids];
};

/**
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>}
 */
const fetchCart = async (signal) => {
  const response = await fetch(getCartJsonUrl(), {
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Cart fetch failed: ${response.status}`);
  }

  return response.json();
};

/**
 * @param {Response} response
 * @param {string} action
 * @returns {Promise<any>}
 */
const readCartResponse = async (response, action) => {
  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `${action} failed: ${response.status}`);
  }

  return data;
};

/**
 * @param {object} payload
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>}
 */
const postCartUpdate = async (payload, signal) => {
  const response = await fetch(Theme.routes.cart_update_url, {
    ...fetchConfig('json', { body: JSON.stringify(payload) }),
    signal,
  });
  return readCartResponse(response, 'Cart update');
};

/**
 * Persist pickup as cart attributes only (no line item properties).
 * @param {any} cart
 * @param {{method: string, storeId?: string, storeName?: string}} pickup
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>}
 */
const persistPickupState = async (cart, pickup, signal) => {
  if (signal?.aborted) return cart;

  const nextAttributes = buildPickupAttributes(pickup);
  const attributes = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
  const attributesChanged =
    String(attributes[ATTR_METHOD] || '') !== nextAttributes[ATTR_METHOD] ||
    String(attributes[ATTR_STORE_ID] || '') !== nextAttributes[ATTR_STORE_ID] ||
    String(attributes[ATTR_STORE_NAME] || '') !== nextAttributes[ATTR_STORE_NAME] ||
    String(attributes[ATTR_HERMES_WAREHOUSE] || '') !== nextAttributes[ATTR_HERMES_WAREHOUSE] ||
    String(attributes[ATTR_HERMES_STORE] || '') !== nextAttributes[ATTR_HERMES_STORE];

  rememberPickup({
    method: nextAttributes[ATTR_METHOD],
    storeId: nextAttributes[ATTR_STORE_ID],
    storeName: nextAttributes[ATTR_STORE_NAME],
  });

  if (!attributesChanged) return cart;
  if (signal?.aborted) return cart;

  const sectionIds = getCartSectionIds();
  /** @type {Record<string, unknown>} */
  const updatePayload = {
    attributes: nextAttributes,
  };

  if (sectionIds.length > 0) {
    updatePayload.sections = sectionIds.join(',');
    updatePayload.sections_url = window.location.pathname;
  }

  const lastResponse = await postCartUpdate(updatePayload, signal);

  document.dispatchEvent(
    new CartUpdateEvent(lastResponse, SOURCE, {
      source: SOURCE,
      sections: lastResponse.sections,
      itemCount: lastResponse.item_count,
    })
  );

  return lastResponse;
};

/**
 * @typedef {object} CartPickupRefs
 * @property {HTMLInputElement} methodStandard
 * @property {HTMLInputElement} methodPickup
 * @property {HTMLElement} storeField
 * @property {HTMLInputElement} storeSearch
 * @property {HTMLElement} storeList
 * @property {HTMLElement} [storeNoResults]
 * @property {HTMLElement} [storeHelp]
 * @property {HTMLElement} [status]
 */

/**
 * @extends {Component<CartPickupRefs>}
 */
class CartPickup extends Component {
  requiredRefs = ['methodStandard', 'methodPickup', 'storeField', 'storeSearch', 'storeList'];

  /** @type {AbortController | null} */
  #active = null;

  /** @type {boolean} */
  #suppressChange = false;

  /** @type {boolean} */
  #showStoreInvalid = false;

  /** @type {number} */
  #activeIndex = -1;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateSelectedLabel();
    this.#reconcileAfterMorph();
    this.#syncCheckout();
    // Delegate from the host so listeners survive summary morphs that replace
    // the list / checkout button nodes without reconnecting this element.
    this.addEventListener('pointerdown', this.#onStoreListPointerDown);
    this.#bindCheckoutGuard();
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindCheckoutGuard();
    this.removeEventListener('pointerdown', this.#onStoreListPointerDown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Cart section morphs replace the summary and clear the combobox value unless Liquid
   * already painted it. Re-apply the remembered store so the selector keeps the choice.
   * @param {Event} [_event]
   */
  #onCartUpdate = (_event) => {
    if (this.#active) return;

    requestAnimationFrame(() => {
      if (!this.isConnected || this.#active) return;
      this.#bindCheckoutGuard();
      this.#reconcileAfterMorph();
      this.#syncCheckout();
    });
  };

  /** @type {HTMLElement | null} */
  #checkoutGuardRoot = null;

  #bindCheckoutGuard() {
    const ctas = this.#checkoutActions();
    if (ctas === this.#checkoutGuardRoot) return;

    this.#unbindCheckoutGuard();
    this.#checkoutGuardRoot = ctas;
    ctas?.addEventListener('click', this.#onCheckoutActionsClick);
  }

  #unbindCheckoutGuard() {
    this.#checkoutGuardRoot?.removeEventListener('click', this.#onCheckoutActionsClick);
    this.#checkoutGuardRoot = null;
  }

  /**
   * @param {Event} event
   */
  #onCheckoutActionsClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[name="checkout"]')) return;
    this.#guardCheckout(event);
  };

  /**
   * Restore pickup UI after a section morph or initial connect.
   * Never overwrite a newer DOM selection with an older session value.
   */
  #reconcileAfterMorph() {
    if (!this.refs.methodPickup.checked) {
      this.#hydrateSelectedLabel();
      return;
    }

    const selected = this.#selectedStore();
    if (selected.storeId) {
      this.#hydrateSelectedLabel();
      return;
    }

    const remembered = getRememberedPickup();
    if (!remembered) return;

    this.#suppressChange = true;
    try {
      this.#selectStoreById(remembered.storeId, { silent: true });
    } finally {
      this.#suppressChange = false;
    }
  }

  /**
   * @param {Event} event
   */
  preventSubmit = (event) => {
    event.preventDefault();
  };

  /**
   * @param {Event} event
   */
  #guardCheckout = (event) => {
    if (!this.#isCheckoutBlocked()) return;

    event.preventDefault();
    event.stopPropagation();

    if (isPickupPersistInFlight()) {
      const status = this.refs.status;
      if (status && !status.hidden) {
        status.focus?.();
      }
      return;
    }

    this.#showStoreInvalid = true;
    this.#syncCheckout();
    this.refs.storeSearch.focus();
  };

  /**
   * @param {Event} event
   */
  handleMethodChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (this.#suppressChange || event.isTrusted === false) return;

    const method = target.value === 'pickup' ? 'pickup' : 'standard';
    const selected = this.#selectedStore();
    const pickup =
      method === 'pickup'
        ? { method: 'pickup', storeId: selected.storeId, storeName: selected.storeName }
        : { method: 'standard' };

    this.#showStoreInvalid = false;

    rememberPickup(pickup);
    broadcastPickupUi(pickup, this);
    this.#toggleStoreField(method === 'pickup');
    this.#syncCheckout();

    this.#closeStoreList();

    if (method === 'pickup') {
      if (selected.storeId && selected.storeName) {
        void this.#persist(pickup);
      }
      return;
    }

    void this.#persist(pickup);
  };

  /**
   * @param {Event} event
   */
  handleStoreSearch = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (this.#suppressChange) return;

    const query = target.value;
    this.#filterStoreOptions(query);
    this.#openStoreList();
    this.#setActiveOption(0);

    if (!query.trim()) {
      for (const option of this.#allOptions()) {
        option.removeAttribute('data-selected');
        option.setAttribute('aria-selected', 'false');
      }

      this.#showStoreInvalid = true;

      const pickup = { method: 'pickup' };
      rememberPickup(pickup);
      broadcastPickupUi(pickup, this);
      this.#syncCheckout();
    }
  };

  handleStoreFocus = () => {
    this.#openStorePicker();
  };

  handleStoreClick = () => {
    this.#openStorePicker();
  };

  #openStorePicker() {
    if (this.refs.storeField.hidden) return;

    this.#filterStoreOptions('');
    this.#openStoreList();
    this.#activeIndex = -1;

    const search = this.refs.storeSearch;
    requestAnimationFrame(() => {
      if (document.activeElement === search) {
        search.select();
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleStoreKeydown = (event) => {
    const options = this.#visibleOptions();

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        this.#openStoreList();
        this.#setActiveOption(this.#activeIndex < 0 ? 0 : this.#activeIndex + 1);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.#openStoreList();
        this.#setActiveOption(this.#activeIndex <= 0 ? options.length - 1 : this.#activeIndex - 1);
        break;
      }
      case 'Enter': {
        const active = options[this.#activeIndex];
        if (!active) return;
        event.preventDefault();
        this.#chooseStoreOption(active);
        break;
      }
      case 'Escape': {
        if (this.refs.storeList.hidden) return;
        event.preventDefault();
        this.#closeStoreList();
        break;
      }
      default:
        break;
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onStoreListPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const list = this.refs.storeList;
    if (!(list instanceof HTMLElement) || list.hidden) return;

    const option = target.closest('[role="option"]');
    if (!(option instanceof HTMLElement) || !list.contains(option)) return;
    if (option.hidden) return;

    event.preventDefault();
    event.stopPropagation();
    this.#chooseStoreOption(option);
  };

  /**
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (this.refs.storeList.hidden) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.contains(target)) return;
    this.#closeStoreList();
  };

  /**
   * @param {{method: string, storeId?: string, storeName?: string}} pickup
   */
  applyUiState(pickup) {
    this.#suppressChange = true;

    try {
      const isPickup = pickup.method === 'pickup';
      this.refs.methodPickup.checked = isPickup;
      this.refs.methodStandard.checked = !isPickup;
      this.#toggleStoreField(isPickup);

      if (isPickup && pickup.storeId) {
        this.#selectStoreById(pickup.storeId, { silent: true });
      } else if (!isPickup) {
        this.#clearStoreSelection({ silent: true });
      }

      this.#syncCheckout();
    } finally {
      this.#suppressChange = false;
    }
  }

  /**
   * @param {boolean} visible
   */
  #toggleStoreField(visible) {
    this.refs.storeField.hidden = !visible;

    if (visible) {
      this.refs.storeField.removeAttribute('inert');
      return;
    }

    this.refs.storeField.setAttribute('inert', '');
    this.#closeStoreList();
  }

  #hydrateSelectedLabel() {
    const selected = this.refs.storeList.querySelector('[data-selected], [aria-selected="true"]');
    if (!(selected instanceof HTMLElement)) return;

    const label = selected.dataset.storeLabel || selected.textContent?.trim() || '';
    if (label) this.refs.storeSearch.value = label;
  }

  /**
   * @returns {{storeId: string, storeName: string}}
   */
  #selectedStore() {
    const selected = this.refs.storeList.querySelector('[data-selected], [aria-selected="true"]');
    if (!(selected instanceof HTMLElement)) {
      return { storeId: '', storeName: '' };
    }

    const storeId = selected.dataset.storeId?.trim() || '';
    const storeName =
      selected.dataset.storeLabel?.trim() ||
      selected.dataset.storeName?.trim() ||
      selected.textContent?.trim() ||
      '';

    if (!storeId || !storeName) {
      return { storeId: '', storeName: '' };
    }

    return { storeId, storeName };
  }

  /**
   * @param {string} storeId
   * @param {{silent?: boolean}} [options]
   */
  #selectStoreById(storeId, options = {}) {
    const option = this.#allOptions().find((item) => item.dataset.storeId === storeId);
    if (!option) return;

    this.#markSelectedOption(option);
    this.refs.storeSearch.value = option.dataset.storeLabel || option.textContent?.trim() || '';
    this.#closeStoreList();

    if (!options.silent) {
      this.#commitSelectedStore();
    }
  }

  /**
   * @param {{silent?: boolean}} [options]
   */
  #clearStoreSelection(options = {}) {
    for (const option of this.#allOptions()) {
      option.removeAttribute('data-selected');
      option.setAttribute('aria-selected', 'false');
    }

    this.refs.storeSearch.value = '';
    this.#closeStoreList();

    if (!options.silent) {
      this.#commitSelectedStore();
    }
  }

  /**
   * @param {HTMLElement} option
   */
  #chooseStoreOption(option) {
    const storeId = option.dataset.storeId?.trim() || '';
    const storeName =
      option.dataset.storeLabel?.trim() ||
      option.dataset.storeName?.trim() ||
      option.textContent?.trim() ||
      '';
    if (!storeId || !storeName) return;

    const pickup = { method: 'pickup', storeId, storeName };
    rememberPickup(pickup);

    this.#markSelectedOption(option);
    this.refs.storeSearch.value = option.dataset.storeLabel || option.textContent?.trim() || '';
    this.#showStoreInvalid = false;
    this.#closeStoreList();
    this.#commitSelectedStore(pickup);
  }

  /**
   * @param {HTMLElement} option
   */
  #markSelectedOption(option) {
    for (const item of this.#allOptions()) {
      const isSelected = item === option;
      item.toggleAttribute('data-selected', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      item.hidden = false;
    }
  }

  /**
   * @param {{storeId: string, storeName: string}} [explicit]
   */
  #commitSelectedStore(explicit) {
    if (this.#suppressChange) return;

    const selected = explicit?.storeId && explicit?.storeName ? explicit : this.#selectedStore();
    if (!selected.storeId || !selected.storeName) return;

    const pickup = {
      method: 'pickup',
      storeId: selected.storeId,
      storeName: selected.storeName,
    };

    rememberPickup(pickup);
    broadcastPickupUi(pickup, this);
    this.#syncCheckout();
    void this.#persist(pickup);
  }

  /**
   * @param {string} query
   */
  #filterStoreOptions(query) {
    const normalized = query.trim().toLowerCase();
    let visibleCount = 0;

    for (const option of this.#allOptions()) {
      const haystack = option.dataset.search || option.textContent?.toLowerCase() || '';
      const match = !normalized || haystack.includes(normalized);
      option.hidden = !match;
      if (match) visibleCount += 1;
    }

    const noResults = this.refs.storeNoResults;
    if (noResults) {
      noResults.hidden = visibleCount > 0;
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #allOptions() {
    return [...this.refs.storeList.querySelectorAll('[role="option"]')].filter(
      (item) => item instanceof HTMLElement
    );
  }

  /**
   * @returns {HTMLElement[]}
   */
  #visibleOptions() {
    return this.#allOptions().filter((item) => !item.hidden);
  }

  #openStoreList() {
    this.refs.storeList.hidden = false;
    this.refs.storeSearch.setAttribute('aria-expanded', 'true');
  }

  #closeStoreList() {
    this.refs.storeList.hidden = true;
    this.refs.storeSearch.setAttribute('aria-expanded', 'false');
    this.#activeIndex = -1;

    for (const option of this.#allOptions()) {
      option.removeAttribute('data-active');
      option.hidden = false;
    }

    const selected = this.refs.storeList.querySelector('[data-selected], [aria-selected="true"]');
    if (selected instanceof HTMLElement) {
      this.refs.storeSearch.value = selected.dataset.storeLabel || selected.textContent?.trim() || '';
    }

    const noResults = this.refs.storeNoResults;
    if (noResults) noResults.hidden = true;
  }

  /**
   * @param {number} index
   */
  #setActiveOption(index) {
    const options = this.#visibleOptions();
    if (!options.length) {
      this.#activeIndex = -1;
      this.refs.storeSearch.removeAttribute('aria-activedescendant');
      return;
    }

    const nextIndex = ((index % options.length) + options.length) % options.length;
    this.#activeIndex = nextIndex;

    for (const [i, option] of options.entries()) {
      option.toggleAttribute('data-active', i === nextIndex);
    }

    const active = options[nextIndex];
    if (active?.id) {
      this.refs.storeSearch.setAttribute('aria-activedescendant', active.id);
      const list = this.refs.storeList;
      const optionTop = active.offsetTop;
      const optionBottom = optionTop + active.offsetHeight;
      if (optionTop < list.scrollTop) {
        list.scrollTop = optionTop;
      } else if (optionBottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = optionBottom - list.clientHeight;
      }
    }
  }

  /**
   * @returns {boolean}
   */
  #isCheckoutBlocked() {
    if (isPickupPersistInFlight() || this.#active) return true;
    return this.refs.methodPickup.checked && !this.#selectedStore().storeId;
  }

  refreshCheckoutLock() {
    this.#syncCheckout();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #checkoutActions() {
    const sibling = this.nextElementSibling;
    if (sibling instanceof HTMLElement && sibling.classList.contains('cart__ctas')) {
      return sibling;
    }

    const parent = this.parentElement;
    const nested = parent?.querySelector('.cart__ctas');
    return nested instanceof HTMLElement ? nested : null;
  }

  #syncCheckout() {
    const syncing = isPickupPersistInFlight() || Boolean(this.#active);
    const missingStore = this.refs.methodPickup.checked && !this.#selectedStore().storeId;
    const blocked = syncing || missingStore;
    const showInvalid = missingStore && this.#showStoreInvalid && !syncing;
    const ctas = this.#checkoutActions();
    const search = this.refs.storeSearch;
    const help = this.refs.storeHelp;

    if (this.refs.methodPickup.checked) {
      search.setAttribute('aria-required', 'true');
      if (showInvalid) {
        search.setAttribute('aria-invalid', 'true');
      } else {
        search.removeAttribute('aria-invalid');
      }
    } else {
      search.removeAttribute('aria-required');
      search.removeAttribute('aria-invalid');
    }

    if (help) {
      const blockedMessage = this.dataset.blockedMessage || '';
      const helpMessage = this.dataset.helpMessage || '';
      const chooseMessage = this.dataset.chooseMessage || helpMessage;
      const updatingMessage = this.dataset.updatingMessage || helpMessage;
      help.textContent = syncing
        ? updatingMessage
        : showInvalid
          ? blockedMessage
          : missingStore
            ? chooseMessage
            : helpMessage;
    }

    if (!ctas) return;

    ctas.classList.toggle('cart__ctas--pickup-blocked', blocked);
    ctas.toggleAttribute('aria-busy', syncing);

    const checkout = ctas.querySelector('[name="checkout"]');
    if (checkout instanceof HTMLButtonElement) {
      checkout.disabled = blocked;
      if (syncing) {
        checkout.setAttribute('aria-disabled', 'true');
      } else {
        checkout.removeAttribute('aria-disabled');
      }
    }

    const accelerated = ctas.querySelector('.additional-checkout-buttons');
    if (accelerated instanceof HTMLElement) {
      accelerated.hidden = blocked;
    }
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.refs.methodStandard.disabled = busy;
    this.refs.methodPickup.disabled = busy;

    const status = this.refs.status;
    if (status) {
      status.hidden = !busy;
      if (busy) {
        status.setAttribute('tabindex', '-1');
      } else {
        status.removeAttribute('tabindex');
      }
    }

    this.#syncCheckout();
    syncAllPickupCheckouts();
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const status = this.refs.status;
    if (!status) return;

    status.hidden = false;
    status.textContent = message;
  }

  /**
   * @param {{method: string, storeId?: string, storeName?: string}} pickup
   */
  async #persist(pickup) {
    this.#active?.abort();
    const abortController = new AbortController();
    this.#active = abortController;
    pickupPersistDepth += 1;
    this.#setBusy(true);
    let failed = false;

    try {
      await enqueuePickupTask(async () => {
        if (abortController.signal.aborted) return;

        const cart = await fetchCart(abortController.signal);
        if (abortController.signal.aborted) return;

        await persistPickupState(cart, pickup, abortController.signal);
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      failed = true;
    } finally {
      pickupPersistDepth = Math.max(0, pickupPersistDepth - 1);

      if (this.#active === abortController) {
        this.#active = null;
        this.#setBusy(false);
        this.#bindCheckoutGuard();
        this.#reconcileAfterMorph();
        this.#syncCheckout();
        syncAllPickupCheckouts();

        if (failed) {
          this.#showError(this.dataset.errorMessage || '');
        }
      } else {
        syncAllPickupCheckouts();
      }
    }
  }
}

if (!customElements.get('cart-pickup')) {
  customElements.define('cart-pickup', CartPickup);
}

/**
 * @param {{method: string, storeId?: string, storeName?: string}} pickup
 * @param {CartPickup} [origin]
 */
const broadcastPickupUi = (pickup, origin) => {
  for (const el of document.querySelectorAll('cart-pickup')) {
    if (el === origin || !(el instanceof CartPickup)) continue;
    el.applyUiState(pickup);
  }
};
